import { useEffect, useRef } from 'react';
import {
  AnyLayer,
  Expression,
  Layer as ILayer,
  LineLayout,
  LinePaint,
  FillLayout,
  FillPaint,
  BackgroundLayout,
  BackgroundPaint,
  CircleLayout,
  CirclePaint,
  FillExtrusionLayout,
  FillExtrusionPaint,
  HeatmapLayout,
  HeatmapPaint,
  HillshadeLayout,
  HillshadePaint,
  RasterLayout,
  RasterPaint,
  SymbolLayout,
  SymbolPaint
} from 'mapbox-gl';

import { useMap } from './useMap';
import { useRerenderOnMapStyleLoad } from './useRerenderOnMapStyleLoad';
import {
  updateLayout,
  updatePaint,
  updateFilter,
  updateBeforeId
} from './Layer.helpers';

/**
 * For more detail on available options look at the docs
 * @url https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/
 */

interface BaseLayerProps
  // Could not any reference in mapbox-gl we are using that a layer has  `ref` and
  // `interactive` options... not sure why they are in the types as I can't find this
  // options in docs
  extends Omit<ILayer, 'ref' | 'interactive'> {
  /**
   * Name of a source description to be used for this layer. Required for all layer types
   * except `background`.
   */
  source: string;

  /**
   * An expression specifying conditions on source features. Only features that match the
   * filter are displayed. Zoom expressions in filters are only evaluated at integer zoom
   * levels. The `["feature-state", ...]` expression is not supported in filter
   * expressions. The `["pitch"]` and `["distance-from-center"]` expressions are supported
   * only for filter expressions on the symbol layer.
   */
  filter?: Expression;

  /**
   * The ID of an existing layer to insert the new layer before. When viewing the map, the
   * id layer will appear beneath the beforeId layer. If beforeId is omitted, the layer
   * will be appended to the end of the layers array and appear above all other layers on
   * the map.
   */
  beforeId?: string;
}

export interface BackgroundLayer extends Omit<BaseLayerProps, 'source'> {
  type: 'background';
  layout?: BackgroundLayout;
  paint?: BackgroundPaint;
}

export interface CircleLayer extends BaseLayerProps {
  type: 'circle';
  layout?: CircleLayout;
  paint?: CirclePaint;
}

export interface FillExtrusionLayer extends BaseLayerProps {
  type: 'fill-extrusion';
  layout?: FillExtrusionLayout;
  paint?: FillExtrusionPaint;
}

export interface FillLayer extends BaseLayerProps {
  type: 'fill';
  layout?: FillLayout;
  paint?: FillPaint;
}

export interface HeatmapLayer extends BaseLayerProps {
  type: 'heatmap';
  layout?: HeatmapLayout;
  paint?: HeatmapPaint;
}

export interface HillshadeLayer extends BaseLayerProps {
  type: 'hillshade';
  layout?: HillshadeLayout;
  paint?: HillshadePaint;
}

export interface LineLayer extends BaseLayerProps {
  type: 'line';
  layout?: LineLayout;
  paint?: LinePaint;
}

export interface RasterLayer extends BaseLayerProps {
  type: 'raster';
  layout?: RasterLayout;
  paint?: RasterPaint;
}

export interface SymbolLayer extends BaseLayerProps {
  type: 'symbol';
  layout?: SymbolLayout;
  paint?: SymbolPaint;
}

export type LayerProps =
  | BackgroundLayer
  | CircleLayer
  | FillExtrusionLayer
  | FillLayer
  | HeatmapLayer
  | HillshadeLayer
  | LineLayer
  | RasterLayer
  | SymbolLayer;

export type LayersWithSource = Exclude<LayerProps, BackgroundLayer>;

export const Layer = (props: LayerProps) => {
  const id = props.id;

  // This ref is used to compare props on each render to determine what needs to be
  // updated, we are not using useEffect or other React hooks to manage layer updates
  // because mapbox-gl has it's own update lifecycle so we delegate all update to
  // mapbox-gl and do not try to control it with React lifecycle methods
  const prevPropsRef = useRef(props);

  const map = useMap();
  //
  // Triggers a re-render when map style changes so we can add back the source
  //
  useRerenderOnMapStyleLoad();

  useEffect(() => {
    return () => {
      // The Map interface does not have a style property, but the Mapbox GL JS map
      // object does.
      // @ts-expect-error
      if (map && Boolean(map.style) && map.getLayer(id)) {
        map.removeLayer(id);
      }
    };
  }, [map, id]);

  /**
   * Make sure the style is loaded before trying to add the source to the map as adding
   * source or layer to the map while the styles is not loaded will throw an error
   */
  if (
    !map ||
    // @ts-expect-error accessing private property
    !map.style?._loaded ||
    ((props as LayersWithSource).source &&
      !map.getSource((props as LayersWithSource).source))
  ) {
    return null;
  }

  /**
   * Handle adding layer
   */
  const hasLayer = !!map.getLayer(id);
  if (!hasLayer) {
    const { beforeId, ...remainingProps } = props;
    map.addLayer(
      {
        ...remainingProps,
        id
      } as AnyLayer,
      beforeId
    );

    return null;
  }

  /**
   * Handle updating layer
   */
  if (props.type !== prevPropsRef.current.type) {
    // TODO: do we want to throw an error here?
    console.error(
      'Changing layer `type` after layer is added is not supported'
    );
  }

  updateLayout(map, id, props.layout, prevPropsRef.current.layout);
  updatePaint(map, id, props.paint, prevPropsRef.current.paint);
  updateFilter(map, id, props.filter, prevPropsRef.current.filter);
  updateBeforeId(map, id, props.beforeId, prevPropsRef.current.beforeId);

  prevPropsRef.current = props;

  return null;
};

export default Layer;
